import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { api } from "../../../../../../../../services/Api/api";
import { OrganizationApi } from "../../../../../../../../services/Api/types";
import OrganizationPreview from "../../../../../../components/Admin/Organization/OrganizationPreview";
import { OrganizationPreviewRequests } from "../../../../../../components/Admin/Organization/services/types";

const organizationPreviewRequests = {
	get: async (id: number) => {
		const params: Record<string, any> = {};
		params.include =
			"applications,applications.accesses,applications.properties,partners,place,custom_fields,terminals,stats";
		return adminApi.getAdminOrganization(id, params);
	},
	users: async (id: number) => adminApi.getAdminOrganizationUsers(id),
	intercomConversations: async (id: number) => {
		const params: Record<string, any> = {
			size: 5,
			sort: "-updated_at",
		};
		return adminApi.getIntercomConversations(id, params);
	},
	subscriptions: async (id: number) => adminApi.getSubscription(id),
	terminals: async (id: number) => adminApi.getTerminals(id),
	invoices: async (id: number) => adminApi.getInvoices(id),
	resendInvitation: async (organizationId: number, invitationId: number) =>
		adminApi.resendInvitation(organizationId, invitationId),
	extendPaymentDate: async (id: number) => adminApi.extendSubscriptionPayment(id),
	exportProformaInvoicePdf: async (organizationId: number, proformaId: string) =>
		adminApi.getSubscriptionProformaPdf(organizationId, proformaId),
	updateFeatures: (organizationId: number, data: any) => adminApi.updateOrganizationFeatures(organizationId, data),
	getFeatures: (organizationId: number) => adminApi.getOrganizationFeatures(organizationId),
} as OrganizationPreviewRequests;

interface MatchParams {
	id: string;
}

const SupportOrganizationPreviewPage = (props: RouteComponentProps) => {
	const [organization, setOrganization] = useState<OrganizationApi | undefined>();
	const { addFlash } = useFlash();
	const { id } = useParams<MatchParams>();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleInit();
	}, []);

	const handleInit = async () => {
		await fetchOrganization();
		await fetchResourceConfig();
	};

	const fetchOrganization = async () => {
		try {
			const response = await organizationPreviewRequests.get(Number(id));
			setOrganization(response);
			handleChangeTabTitle(`${response.name} | ${t("lib:common.word.locations")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchResourceConfig = async () => {
		try {
			const params = {
				resource_name: "ADMIN_ORGANIZATION",
				custom_field_resource_type: "ORGANIZATION",
				custom_field_permission_scope: "WRITE",
			};
			const res = await api.getResourceConfig(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!organization || !customFieldsConfig) {
		return <LoadingContainer />;
	}

	return (
		<div className="content">
			<OrganizationPreview
				organization={organization}
				url={props.match.url}
				refresh={fetchOrganization}
				context={"SUPPORT"}
				requests={organizationPreviewRequests}
				customFieldsConfig={customFieldsConfig}
			/>
		</div>
	);
};

export default SupportOrganizationPreviewPage;
